import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2, Loader2, ChevronDown, ChevronRight, RotateCcw, Sparkles } from 'lucide-react';

/**
 * Per-brand Theme Bank editor.
 * The theme bank is the list of TOPICS the AI rotates through when it generates this
 * brand's 90-day plan (videos + posts). Built-in defaults can be hidden/removed and the
 * user can add their own themes — additions/removals persist per-brand and are used in the
 * very next generation.
 */
export function ThemeBankEditor({ api, profileId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [newTheme, setNewTheme] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/social-studio/brand-profiles/${profileId}/theme-bank`);
      setData(r.data);
    } catch (e) {
      toast.error('Theme bank load failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [api, profileId]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const addTheme = async () => {
    const t = newTheme.trim();
    if (!t) return;
    setSaving(true);
    try {
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${profileId}/theme-bank/add`, { theme: t });
      setData(r.data);
      setNewTheme('');
      toast.success('Theme added — the AI will use it next time you generate');
    } catch (e) {
      toast.error('Add failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const removeTheme = async (theme) => {
    try {
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${profileId}/theme-bank/remove`, { theme });
      setData(r.data);
    } catch (e) {
      toast.error('Remove failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const restoreTheme = async (theme) => {
    try {
      const r = await api.post(`/api/admin/social-studio/brand-profiles/${profileId}/theme-bank/restore`, { theme });
      setData(r.data);
      toast.success('Default theme restored');
    } catch (e) {
      toast.error('Restore failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const disabledSet = new Set(data?.disabled_themes || []);
  const activeDefaults = (data?.default_themes || []).filter((t) => !disabledSet.has(t));
  const customThemes = data?.custom_themes || [];
  const effectiveCount = (data?.effective_count != null)
    ? data.effective_count : (activeDefaults.length + customThemes.length);

  return (
    <div className="mt-3 border-t border-[#F1F5F9] pt-3" data-testid={`theme-bank-${profileId}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-[#0A0F1C] hover:text-[#1D4ED8] transition-colors"
        data-testid={`theme-bank-toggle-${profileId}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <ListChecks size={13} className="text-[#7C3AED]" aria-hidden="true" />
          Theme Bank
          {data && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F5F3FF] text-[#7C3AED]">{effectiveCount}</span>
          )}
        </span>
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`theme-bank-panel-${profileId}`}>
          <p className="text-[11px] text-[#64748B] leading-snug">
            These are the topics the AI rotates through when it writes this brand's posts and videos.
            Add your own, or remove any you don't want. Changes apply to your next generation.
          </p>

          {/* Add theme */}
          <div className="bg-[#FAF5FF] border border-dashed border-[#DDD6FE] rounded-xl p-3 space-y-2">
            <textarea
              value={newTheme}
              onChange={(e) => setNewTheme(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addTheme(); }}
              placeholder="Add a topic the AI should make content about (e.g. 'why mass layoffs prove you need your own income')"
              rows={2}
              className="w-full px-2.5 py-1.5 border border-[#E2E8F0] rounded-lg text-xs focus:outline-none focus:border-[#7C3AED] resize-y"
              aria-label="New theme"
              data-testid={`theme-bank-input-${profileId}`}
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#94A3B8]">Tip: write it as a topic, not a full caption</span>
              <button
                type="button"
                onClick={addTheme}
                disabled={saving || !newTheme.trim()}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  saving || !newTheme.trim() ? 'bg-[#E2E8F0] text-[#94A3B8] cursor-not-allowed' : 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
                }`}
                data-testid={`theme-bank-add-btn-${profileId}`}
              >
                {saving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Plus size={13} aria-hidden="true" />}
                Add theme
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-xs text-[#94A3B8] py-2 inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" /> Loading…
            </div>
          ) : data ? (
            <>
              {/* Custom themes */}
              {customThemes.length > 0 && (
                <div className="space-y-1.5" data-testid={`theme-bank-custom-${profileId}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] inline-flex items-center gap-1">
                    <Sparkles size={11} aria-hidden="true" /> Your themes ({customThemes.length})
                  </p>
                  <ul className="space-y-1.5">
                    {customThemes.map((t) => (
                      <li key={t} className="flex items-start justify-between gap-2 bg-[#FAF5FF] border border-[#E9D5FF] rounded-lg px-2.5 py-1.5"
                          data-testid="theme-bank-custom-item">
                        <span className="text-xs text-[#0A0F1C] leading-snug">{t}</span>
                        <button type="button" onClick={() => removeTheme(t)} aria-label={`Remove ${t}`}
                                className="text-[#94A3B8] hover:text-red-600 transition-colors shrink-0 mt-0.5"
                                data-testid="theme-bank-remove-custom">
                          <Trash2 size={13} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Built-in defaults */}
              <div className="space-y-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                  Built-in themes ({activeDefaults.length})
                </p>
                <ul className="space-y-1.5" data-testid={`theme-bank-defaults-${profileId}`}>
                  {activeDefaults.map((t) => (
                    <li key={t} className="flex items-start justify-between gap-2 bg-white border border-[#E2E8F0] rounded-lg px-2.5 py-1.5"
                        data-testid="theme-bank-default-item">
                      <span className="text-xs text-[#334155] leading-snug">{t}</span>
                      <button type="button" onClick={() => removeTheme(t)} aria-label={`Remove ${t}`}
                              className="text-[#CBD5E1] hover:text-red-600 transition-colors shrink-0 mt-0.5"
                              data-testid="theme-bank-remove-default">
                        <Trash2 size={13} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Removed defaults (restore) */}
              {(data.disabled_themes || []).length > 0 && (
                <div className="space-y-1.5" data-testid={`theme-bank-removed-${profileId}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                    Removed ({data.disabled_themes.length}) — click to restore
                  </p>
                  <ul className="space-y-1.5">
                    {data.disabled_themes.map((t) => (
                      <li key={t} className="flex items-start justify-between gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 opacity-70">
                        <span className="text-xs text-[#94A3B8] line-through leading-snug">{t}</span>
                        <button type="button" onClick={() => restoreTheme(t)} aria-label={`Restore ${t}`}
                                className="text-[#94A3B8] hover:text-[#059669] transition-colors shrink-0 mt-0.5"
                                data-testid="theme-bank-restore">
                          <RotateCcw size={13} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-[10px] text-[#94A3B8]">{effectiveCount} themes active · the AI rotates these across your plan</p>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ThemeBankEditor;
